import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { debounce } from "lodash";
import { JobType } from "../../apiResources/Job";
import {
  SearchFilterContainer,
  SearchFilterProvider,
  SearchFilterGroup,
  SearchFilterOption,
} from "../library/SearchFilter";
import Employer from "../../apiResources/Employer";
import EmployerUser from "../../apiResources/EmployerUser";
import JobListSelectedFilters from "./JobListSelectedFilters";
import { FilterData } from "./JobListPage";

const JobListFilterBar = (props: Props) => {
  const {
    onQueryChange,
    onFiltersChange,
    filters,
    companies,
    users,
    query: initialQuery,
  } = props;

  const [query, setQuery] = useState(initialQuery);

  const debouncedQueryChange = useMemo(() => debounce(onQueryChange, 350), []);

  useEffect(() => {
    debouncedQueryChange.cancel();
    debouncedQueryChange(query);
  }, [query]);

  // Convert array of EmployerUsers to key:name map for showing on the filter tags.
  const userLabels = users.reduce(
    (labels, user) => ({
      ...labels,
      [user.id]: user.fullName,
    }),
    {}
  );

  // Convert array of Employer to key:name map for showing on the filter tags.
  const companyLabels = companies.reduce(
    (labels, employer) => ({
      ...labels,
      [employer.id]: employer.name,
    }),
    {}
  );

  return (
    <SearchFilterProvider initialValue={filters} onChange={onFiltersChange}>
      <SearchBar id="job-filter-bar">
        <SearchInput
          placeholder="Search for a job"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        <SearchFilterContainer analytics={false}>
          <SearchFilterGroup label="Job Status" defaultCollapsed={false}>
            <SearchFilterOption
              name="status"
              value={[
                "draft",
                "new",
                "interviewing",
                "offer_made",
                "filled",
                "closed",
              ]}
            >
              Select all
            </SearchFilterOption>
            <SearchFilterOption name="status" value="draft">
              Draft
            </SearchFilterOption>
            <SearchFilterOption
              name="status"
              value={["new", "interviewing", "offer_made"]}
            >
              Active
            </SearchFilterOption>
            <SearchFilterOption name="status" value="new" nested>
              New
            </SearchFilterOption>
            <SearchFilterOption name="status" value="interviewing" nested>
              Interviewing
            </SearchFilterOption>
            <SearchFilterOption name="status" value="offer_made" nested>
              Offer made
            </SearchFilterOption>
            <SearchFilterOption name="status" value="filled">
              Filled
            </SearchFilterOption>
            <SearchFilterOption name="status" value="closed">
              Closed
            </SearchFilterOption>
          </SearchFilterGroup>

          <SearchFilterGroup label="Job Type">
            <SearchFilterOption name="type" value={Object.values(JobType)}>
              Select all
            </SearchFilterOption>
            <SearchFilterOption name="type" value={JobType.PERMANENT}>
              Permanent
            </SearchFilterOption>
            <SearchFilterOption name="type" value={JobType.CONTRACT}>
              Contract
            </SearchFilterOption>
          </SearchFilterGroup>

          {companies.length > 1 && (
            <SearchFilterGroup label="Company">
              <SearchFilterOption
                name="companies"
                value={companies.map(({ id }) => id)}
              >
                Select all
              </SearchFilterOption>
              {companies.map((company) => (
                <SearchFilterOption
                  key={company.id}
                  name="companies"
                  value={company.id}
                >
                  {company.name}
                </SearchFilterOption>
              ))}
            </SearchFilterGroup>
          )}

          {users.length > 1 && (
            <SearchFilterGroup label="Users">
              <SearchFilterOption
                name="users"
                value={users.map(({ id }) => id)}
              >
                Select all
              </SearchFilterOption>
              {users.map((user) => (
                <SearchFilterOption key={user.id} name="users" value={user.id}>
                  {user.fullName}
                </SearchFilterOption>
              ))}
            </SearchFilterGroup>
          )}
        </SearchFilterContainer>
      </SearchBar>

      <JobListSelectedFilters
        filters={filters}
        userLabelMap={userLabels}
        companyLabelMap={companyLabels}
      />
    </SearchFilterProvider>
  );
};

const SearchInput = styled.input`
  flex: 1;
  padding: 0.5rem 1rem;
  border-radius: 3px;
  margin-right: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.gray700};
`;

const SearchBar = styled.div`
  display: flex;
  border-radius: 3px;
  margin-bottom: 1rem;
`;

type Props = {
  onQueryChange: (query: string) => void;
  onFiltersChange: (filters: FilterData) => void;
  query: string;
  filters: FilterData;
  companies: Employer[];
  users: EmployerUser[];
};

export default JobListFilterBar;
